import { OpsSignalKind, SignalNature, type OpsDayStatusRecord } from '@navio/nsdk';
import type { NoddiAsyncError } from 'noddi-types';
import { cn, cva, NoddiIcon } from 'noddi-ui-common';
import { ApiErrorMessageInternal, NoddiButton, NoddiCard, NoddiSheet, NoddiSkeleton } from 'noddi-ui-web';
import { OPS_NATURE_BY_KIND, OPS_NATURES, type OpsListSectionId, type OpsSignal } from 'noddi-util';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dayCells, type DayCell } from '../dayCells';
import { useOpsStaffingOverview } from '../hooks/useOpsStaffingOverview';
import { useOpsOverviewRoutes } from '../opsOverviewContext';

interface Props {
  dayStatus: OpsDayStatusRecord | undefined;
  isPending: boolean;
  error: NoddiAsyncError | null;
  signals: OpsSignal[];
  pendingKinds: readonly OpsSignalKind[];
  now: Date;
  onShowKind: (section: OpsListSectionId) => void;
}

const toneVariants = cva('', {
  variants: {
    tone: {
      danger: 'text-dangerDark',
      muted: 'text-muted-foreground'
    }
  }
});

const STATUS_IDS = ['routes', 'route-planning', 'garage-bookings', 'staffing'];

const cellSkeleton = (id: string) => (
  <NoddiSkeleton key={id} className='h-20 w-full rounded-xl last:odd:col-span-2 lg:last:odd:col-span-1' />
);

export const DayStrip = ({ dayStatus, isPending, error, signals, pendingKinds, now, onShowKind }: Props) => {
  const routes = useOpsOverviewRoutes();
  const navigate = useNavigate();
  const [openId, setOpenId] = useState<string | null>(null);
  const staffingOverview = useOpsStaffingOverview();

  const routesLoading: boolean = pendingKinds.includes(OpsSignalKind.ROUTE_BEHIND_SCHEDULE);
  const loadingNatures: SignalNature[] = OPS_NATURES.map(({ nature }) => nature).filter((nature) =>
    pendingKinds.some((kind) => OPS_NATURE_BY_KIND[kind] === nature)
  );

  const cells: DayCell[] = dayCells({
    dayStatus,
    signals,
    staffing: staffingOverview.enabled ? staffingOverview.summary : undefined,
    now,
    routes,
    navigate: (to) => void navigate(to),
    onShowKind
  });
  const routesWaiting: boolean = routesLoading && (dayStatus?.routesToday ?? 0) > 0;
  const statusCells: DayCell[] = cells.filter(
    (cell) => STATUS_IDS.includes(cell.id) && !(routesWaiting && cell.id === 'routes')
  );
  const open: DayCell | undefined = cells.find((cell) => cell.id === openId);
  const staffingWaiting: boolean = staffingOverview.enabled && staffingOverview.isPending;
  const slots: { id: string; cell?: DayCell }[] = [
    ...(isPending ? [{ id: 'day-status' }] : []),
    ...(routesWaiting ? [{ id: 'routes' }] : []),
    ...(staffingWaiting ? [{ id: 'staffing' }] : []),
    ...statusCells.map((cell) => ({ id: cell.id, cell })),
    ...OPS_NATURES.flatMap(({ nature }): { id: string; cell?: DayCell }[] => {
      if (loadingNatures.includes(nature)) {
        return [{ id: nature }];
      }
      const cell: DayCell | undefined = cells.find((entry) => entry.id === nature);
      return cell ? [{ id: cell.id, cell }] : [];
    })
  ];

  return (
    <>
      {error && <ApiErrorMessageInternal error={error} />}
      {staffingOverview.error && <ApiErrorMessageInternal error={staffingOverview.error} />}
      {slots.length > 0 && (
        <div className='grid grid-cols-[repeat(auto-fit,minmax(16rem,1fr))] gap-4'>
          {slots.map((slot) =>
            slot.cell ? (
              <NoddiCard key={slot.id} className='overflow-hidden p-0' contentClassName='p-0'>
                <button
                  type='button'
                  onClick={() => setOpenId(slot.cell?.id ?? null)}
                  className='flex w-full min-w-0 items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted'
                >
                  <span className='flex size-9 shrink-0 items-center justify-center rounded-md bg-lightPurple text-darkPurple'>
                    <NoddiIcon name={slot.cell.icon} size='small' />
                  </span>
                  <span className='flex min-w-0 flex-col gap-0.5'>
                    <span className='truncate text-xs font-medium text-muted-foreground'>{slot.cell.title}</span>
                    <span className='flex items-baseline gap-1.5'>
                      <span className='text-xl font-semibold tabular-nums'>{slot.cell.value}</span>
                      <span className='truncate text-sm text-muted-foreground'>{slot.cell.unit}</span>
                    </span>
                    <span className={cn('truncate text-xs', toneVariants({ tone: slot.cell.noteTone }))}>
                      {slot.cell.note}
                    </span>
                  </span>
                </button>
              </NoddiCard>
            ) : (
              cellSkeleton(slot.id)
            )
          )}
        </div>
      )}
      <NoddiSheet
        open={open !== undefined}
        onOpenChange={(isOpen) => !isOpen && setOpenId(null)}
        title={open?.title}
        footer={
          open?.action && (
            <NoddiButton
              className='w-full'
              onClick={() => {
                setOpenId(null);
                open.action?.run();
              }}
            >
              {open.action.label}
            </NoddiButton>
          )
        }
      >
        {open && (
          <dl className='flex flex-col divide-y divide-border'>
            {open.details.map((detail) => (
              <div key={detail.label} className='flex items-center justify-between gap-4 py-2.5 text-sm'>
                <dt className='text-muted-foreground'>{detail.label}</dt>
                <dd className={cn('font-semibold tabular-nums', toneVariants({ tone: detail.tone }))}>
                  {detail.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </NoddiSheet>
    </>
  );
};
